import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { Invitation } from '../types/firestore';

const InvitationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<string[]>([]);

  // Form state
  const [userType, setUserType] = useState<'startup' | 'investor'>('startup');
  const [expiresInDays, setExpiresInDays] = useState<number>(30);
  const [count, setCount] = useState<number>(1);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      setError(null);

      const listInvitationsFunction = httpsCallable(functions, 'listInvitations');
      const result = await listInvitationsFunction({});
      const data = result.data as any;

      if (data?.success && data.invitations) {
        // Convert date strings or timestamp objects to Date objects
        const parseDate = (dateValue: any): Date | null => {
          if (!dateValue) return null;

          // Handle serialized Firestore Timestamp object
          if (dateValue && typeof dateValue === 'object') {
            if (dateValue._seconds !== undefined) {
              const seconds = dateValue._seconds || 0;
              const nanoseconds = dateValue._nanoseconds || 0;
              const milliseconds = seconds * 1000 + nanoseconds / 1000000;
              return new Date(milliseconds);
            }
            if (dateValue.seconds !== undefined) {
              const seconds = dateValue.seconds || 0;
              const nanoseconds = dateValue.nanoseconds || 0;
              const milliseconds = seconds * 1000 + nanoseconds / 1000000;
              return new Date(milliseconds);
            }
            if (dateValue.toDate && typeof dateValue.toDate === 'function') {
              try {
                return dateValue.toDate();
              } catch {
                return null;
              }
            }
          }

          // Handle ISO string
          if (typeof dateValue === 'string') {
            try {
              const date = new Date(dateValue);
              return isNaN(date.getTime()) ? null : date;
            } catch {
              return null;
            }
          }

          // Handle Date objects
          if (dateValue instanceof Date) {
            return isNaN(dateValue.getTime()) ? null : dateValue;
          }

          return null;
        };

        const invitationsWithDates = data.invitations.map((inv: any) => ({
          ...inv,
          expiresAt: parseDate(inv.expiresAt),
          usedAt: inv.usedAt ? parseDate(inv.usedAt) : undefined,
          createdAt: parseDate(inv.createdAt),
        }));

        setInvitations(invitationsWithDates);
      } else {
        setError(data?.error || 'Failed to load invitations');
      }
    } catch (err: any) {
      console.error('Error loading invitations:', err);
      setError(err.message || 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const parseDate = useMemo(
    () =>
      (dateValue: any): Date | null => {
        if (!dateValue) return null;

        // Handle serialized Firestore Timestamp object
        if (dateValue && typeof dateValue === 'object') {
          if (dateValue._seconds !== undefined) {
            const seconds = dateValue._seconds || 0;
            const nanoseconds = dateValue._nanoseconds || 0;
            const milliseconds = seconds * 1000 + nanoseconds / 1000000;
            return new Date(milliseconds);
          }
          if (dateValue.seconds !== undefined) {
            const seconds = dateValue.seconds || 0;
            const nanoseconds = dateValue.nanoseconds || 0;
            const milliseconds = seconds * 1000 + nanoseconds / 1000000;
            return new Date(milliseconds);
          }
          if (dateValue.toDate && typeof dateValue.toDate === 'function') {
            try {
              return dateValue.toDate();
            } catch {
              return null;
            }
          }
        }

        // Handle ISO string
        if (typeof dateValue === 'string') {
          try {
            const date = new Date(dateValue);
            return isNaN(date.getTime()) ? null : date;
          } catch {
            return null;
          }
        }

        // Handle Date objects
        if (dateValue instanceof Date) {
          return isNaN(dateValue.getTime()) ? null : dateValue;
        }

        return null;
      },
    []
  );

  const handleCreateInvitations = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setCreating(true);
      setError(null);
      setSuccess(null);
      setRecentlyCreated([]);

      const createInvitationFunction = httpsCallable(functions, 'createInvitation');
      const result = await createInvitationFunction({
        userType,
        expiresInDays,
        count,
      });
      const data = result.data as any;

      if (data?.success) {
        const createdCodes: string[] =
          (data.invitations || []).map((i: any) => i.code).filter(Boolean) ?? [];
        setRecentlyCreated(createdCodes);
        setSuccess(`Successfully created ${data.invitations?.length || count} invitation(s)!`);
        // Reload invitations
        await loadInvitations();
        // Reset form
        setCount(1);
        setExpiresInDays(30);
      } else {
        setError(data?.error || 'Failed to create invitations');
      }
    } catch (err: any) {
      console.error('Error creating invitations:', err);
      setError(err.message || 'Failed to create invitations');
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Invitation code copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'used':
        return 'bg-green-500';
      case 'expired':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const isExpired = (expiresAt: Date | null) => {
    if (!expiresAt) return false;
    return new Date() > expiresAt;
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#262626' }}
      >
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4"></div>
          <p className="text-white text-lg">Loading invitations...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center px-4 pt-10 pb-12"
      style={{ backgroundColor: '#262626' }}
    >
      {/* Top navigation (matches old prairi-web: centered, small text) */}
      <div className="w-11/12 md:max-w-2xl mb-4 flex justify-between">
        <button
          onClick={() => navigate('/admindashboard')}
          className="text-sm text-gray-400 hover:text-white transition-colors"
        >
          ← Admin
        </button>
        <button
          onClick={() => navigate('/users')}
          className="text-sm text-gray-400 hover:text-white transition-colors"
        >
          Users →
        </button>
      </div>

      {/* Main generator card (mirrors prairi-web CodeGenerator styling) */}
      <div
        className="mx-auto w-11/12 md:max-w-2xl rounded-md py-2.5 px-5"
        style={{
          backgroundColor: '#1e1e1e',
          boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.75)',
          borderRadius: '60px',
        }}
      >
        <h1 className="mb-4 text-center text-xl text-gray-500 uppercase">Create Invitation Code</h1>

        {/* Startup / Investor toggle */}
        <div className="flex justify-center gap-2">
          <button
            type="button"
            onClick={() => setUserType('startup')}
            className={`px-4 py-2 rounded-md border transition-all duration-200 ${
              userType === 'startup'
                ? 'border-emerald-500 text-white'
                : 'border-gray-500 text-gray-300'
            }`}
          >
            Startup
          </button>
          <button
            type="button"
            onClick={() => setUserType('investor')}
            className={`px-4 py-2 rounded-md border transition-all duration-200 ${
              userType === 'investor'
                ? 'border-emerald-500 text-white'
                : 'border-gray-500 text-gray-300'
            }`}
          >
            Investor
          </button>
        </div>

        <form className="py-0 px-5" onSubmit={handleCreateInvitations}>
          <div className="mt-5">
            <label className="text-gray-300" htmlFor="expiresInDays">
              Expires in (days)
            </label>
            <input
              id="expiresInDays"
              className="border-blackish mt-2.5 w-full rounded border p-2.5 text-black outline-none"
              type="number"
              min={1}
              max={365}
              value={expiresInDays}
              onChange={e => setExpiresInDays(parseInt(e.target.value) || 30)}
              required
            />
          </div>

          <div className="mt-5">
            <label className="text-gray-300" htmlFor="count">
              Number of codes
            </label>
            <input
              id="count"
              className="border-blackish mt-2.5 w-full rounded border p-2.5 text-black outline-none"
              type="number"
              min={1}
              max={100}
              value={count}
              onChange={e => setCount(parseInt(e.target.value) || 1)}
              required
            />
          </div>

          {error && <p className="text-sm md:text-base my-2 text-red-500">{error}</p>}
          {success && <p className="text-sm md:text-base my-2 text-emerald-400">{success}</p>}

          <div className="my-2.5 mx-0 text-center flex flex-col">
            <input
              type="submit"
              value={creating ? 'CREATING...' : 'GENERATE'}
              disabled={creating}
              className="w-full disabled:opacity-30 uppercase bg-body cursor-pointer mb-4 rounded border border-gray-500 p-2.5 text-base text-white outline-none transition-all duration-300 ease-in-out hover:scale-105"
            />
          </div>
        </form>

        {/* Recently created codes (mirrors copy affordance from old UI) */}
        {recentlyCreated.length > 0 && (
          <div className="px-5 pb-4">
            <h2 className="text-sm text-gray-400 uppercase mb-2 text-center">Newly generated</h2>
            <div className="space-y-2">
              {recentlyCreated.map(code => (
                <div
                  key={code}
                  className="flex items-center justify-between bg-black/40 border border-gray-700 rounded px-3 py-2"
                >
                  <span className="text-white font-mono text-sm">{code}</span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(code)}
                    className="text-sm text-gray-300 hover:text-white transition-colors"
                  >
                    Copy
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* List card (keep existing functionality but style closer to admin table look) */}
      <div className="w-11/12 md:max-w-5xl mt-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">All Invitations</h2>
          <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm font-semibold">
            {invitations.length}
          </span>
        </div>

        {invitations.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No invitations found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-full">
              {/* Header (mirrors UserTable header block style) */}
              <div className="bg-gray-800 border-b border-gray-700">
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-[220px] px-6 py-3">
                    <div className="text-xs font-medium text-gray-300 uppercase tracking-wider text-left">
                      Code
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-[120px] px-6 py-3">
                    <div className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Type
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-[120px] px-6 py-3">
                    <div className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Status
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-[140px] px-6 py-3">
                    <div className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Expires
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-[140px] px-6 py-3">
                    <div className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Created
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-[120px] px-6 py-3">
                    <div className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Action
                    </div>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="divide-y divide-gray-700" style={{ backgroundColor: '#262626' }}>
                {invitations.map(invitation => {
                  const expired = isExpired(invitation.expiresAt);
                  const displayStatus =
                    expired && invitation.status === 'pending' ? 'expired' : invitation.status;

                  return (
                    <div
                      key={invitation.id}
                      className="flex items-center border-b border-gray-700 hover:bg-gray-800 transition-colors"
                    >
                      <div className="flex-shrink-0 w-[220px] px-6 py-4 text-left">
                        <div className="text-sm text-white font-mono">{invitation.code}</div>
                      </div>
                      <div className="flex-shrink-0 w-[120px] px-6 py-4">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            invitation.userType === 'startup'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {invitation.userType}
                        </span>
                      </div>
                      <div className="flex-shrink-0 w-[120px] px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(
                            displayStatus
                          )}`}
                        >
                          {displayStatus}
                        </span>
                      </div>
                      <div className="flex-shrink-0 w-[140px] px-6 py-4 text-sm text-gray-300">
                        {invitation.expiresAt ? invitation.expiresAt.toLocaleDateString() : 'N/A'}
                      </div>
                      <div className="flex-shrink-0 w-[140px] px-6 py-4 text-sm text-gray-300">
                        {invitation.createdAt ? invitation.createdAt.toLocaleDateString() : 'N/A'}
                      </div>
                      <div className="flex-shrink-0 w-[120px] px-6 py-4">
                        <button
                          onClick={() => copyToClipboard(invitation.code)}
                          className="px-3 py-1 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors text-sm"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitationsPage;
